import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import commissionMessagesService from '../../services/commissionMessagesService';

// Voyager Pro reskin (Figma CommissionsPage's "Messages" modal)
const STATUS_CFG = {
  approved: { label: 'Approved', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  paid: { label: 'Paid', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  pending: { label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  denied: { label: 'Denied', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  on_hold: { label: 'On Hold', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  needs_reply: { label: 'Needs Reply', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  needs_agent_reply: { label: 'Needs Agent Reply', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  needs_admin_reply: { label: 'Needs Admin Reply', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  draft: { label: 'Draft', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  // 'under_review' is the real commissions.status ENUM value independent agents'
  // "Processing" option maps to (see backend/routes/commissions.js).
  under_review: { label: 'Processing', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

const CommissionMessagingModal = ({ commission, onClose, onMessageSent, onCommissionUpdate }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Date formatting function to avoid timezone issues
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    if (commission?.id) {
      loadMessages();
      markMessagesAsRead();
    }
  }, [commission?.id]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await commissionMessagesService.getMessages(commission.id);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      alert('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async () => {
    try {
      await commissionMessagesService.markAsRead(commission.id);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);

    try {
      const messageText = newMessage.trim();
      await commissionMessagesService.sendAgentReply(commission.id, messageText);

      // Refresh messages after sending
      await loadMessages();

      setNewMessage('');

      // Auto-scroll to bottom
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);

      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)] flex-shrink-0">
          <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">Commission Messages</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        {/* Commission Summary */}
        <div className="px-6 py-4 bg-[var(--muted)]/40 border-b border-[var(--border)] flex-shrink-0">
          <div className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-widest mb-2">Commission Details</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            <div>
              <div className="text-[var(--muted-foreground)]">Reservation #</div>
              <div className="font-semibold text-[var(--foreground)] font-mono mt-0.5">{commission.reservation_number}</div>
            </div>
            <div>
              <div className="text-[var(--muted-foreground)]">Guest</div>
              <div className="font-semibold text-[var(--foreground)] mt-0.5">{commission.guest_first_name} {commission.guest_last_name}</div>
            </div>
            <div>
              <div className="text-[var(--muted-foreground)]">Supplier</div>
              <div className="font-semibold text-[var(--foreground)] mt-0.5">{commission.supplier}</div>
            </div>
            <div>
              <div className="text-[var(--muted-foreground)]">Travel Date</div>
              <div className="font-medium text-[var(--foreground)] mt-0.5">{formatDateForDisplay(commission.travel_date)}</div>
            </div>
            <div>
              <div className="text-[var(--muted-foreground)]">Gross</div>
              <div className="font-medium text-[var(--foreground)] mt-0.5">{formatCurrency(commission.gross_commission)}</div>
            </div>
            <div>
              <div className="text-[var(--muted-foreground)]">Agent</div>
              <div className="font-medium text-emerald-600 mt-0.5">{formatCurrency(commission.agent_commission)}</div>
            </div>
          </div>
          <div className="mt-2">
            <StatusBadge status={commission.status} />
          </div>
        </div>

        {/* Messages Section */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          <div className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-widest mb-3">Message History</div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <div className="w-8 h-8 border-4 border-[var(--border)] rounded-full animate-spin" style={{ borderTopColor: 'var(--primary)' }} />
              <p className="text-sm text-[var(--muted-foreground)]">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 text-sm text-[var(--muted-foreground)]">No messages yet.</div>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.sender_type === 'agent' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm ${message.sender_type === 'agent' ? 'bg-[var(--primary)] text-white rounded-br-sm' : 'bg-[var(--muted)] text-[var(--foreground)] rounded-bl-sm'}`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className={`text-xs font-semibold ${message.sender_type === 'agent' ? 'text-white/80' : 'text-[var(--muted-foreground)]'}`}>
                        {message.sender_type === 'agent' ? 'You' : message.sender_name}
                      </span>
                      <span className={`text-xs ${message.sender_type === 'agent' ? 'text-white/60' : 'text-[var(--muted-foreground)]'}`}>
                        {formatDate(message.created_at)}
                      </span>
                    </div>
                    <div className="whitespace-pre-wrap">{message.message}</div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Reply Section - Always Available */}
        <div className="px-6 py-4 border-t border-[var(--border)] flex-shrink-0">
          <div className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-widest mb-2">Send Message</div>
          <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <span className="font-semibold">Commission Status:</span>
            <StatusBadge status={commission.status} />
          </div>
          <form onSubmit={(e) => e.preventDefault()}>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message about this commission..."
              rows={3}
              className="w-full px-4 py-3 border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] bg-[var(--muted)]/20 focus:border-[var(--accent)] outline-none transition-colors resize-none mb-3"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="px-4 py-2.5 border border-[var(--border)] rounded-xl text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/50 transition-colors disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="submit"
                onClick={handleSendMessage}
                disabled={sending || !newMessage.trim()}
                className="px-4 py-2.5 bg-[var(--primary)] text-white rounded-xl text-sm font-semibold hover:bg-[var(--primary)]/90 transition-colors disabled:opacity-40"
              >
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommissionMessagingModal;
